import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Footer from "../components/Footer";
import { ensureAnon } from "../services/proposals";
import { analytics, db } from "../firebase";
import { jsPDF } from "jspdf";
import { logEvent } from "firebase/analytics";
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { useMutationAuthGate } from "../hooks/useMutationAuthGate";
import {
  confirmCheckoutSessionServer,
  createCheckoutSessionServer,
  generateDataModelServer,
  generateFeatureDesignServer,
  generateUIDesignServer,
  generateUXScenarioServer,
  registerFreeDownloadServer,
  updateDevPlanFieldsServer,
} from "../services/functions";
import { formatFunctionActionError } from "../services/functionErrors";
import { buildPricingAssessment, formatUsd } from "../utils/pricingEngine";
import { getStoredAttributionPayload } from "../services/marketingAttribution";
import {
  emitCheckoutBeginTracking,
  emitSignupLeadTracking,
  emitPurchaseTracking,
  purchaseTransactionIdFromSession,
} from "../services/conversionTracking";

declare global {
  interface Window {
    ExpoSnack?: {
      initialize?: () => void;
    };
  }
}

type ImproveMode = "cost" | "reduce" | "speed";
type RegenerateTarget = "feature" | "scenario" | "uiDesign" | "dataModel";
type PurchaseTier = "high" | "mid" | "low";
type DevPlanTab = "overview" | "feature" | "scenarios" | "uiDesign" | "dataModel" | "code" | "preview";
type FounderSectionKey =
  | "founderSummary"
  | "mvpScopeNotes"
  | "timelineNotes"
  | "investmentNotes"
  | "experienceSummary"
  | "screensSummary"
  | "technicalSummary";

const DEVPLAN_LOADING_STAGES = [
  "Designing features…",
  "Mapping user flows…",
  "Planning screens…",
  "Preparing data model…",
  "Generating code…",
  "Rendering preview…",
];

function getLoadingStageIndexFromJobStep(step: string): number | null {
  const normalized = step.toLowerCase();
  if (!normalized) return null;
  if (normalized.includes("feature")) return 0;
  if (normalized.includes("flow") || normalized.includes("scenario") || normalized.includes("ux")) return 1;
  if (normalized.includes("screen") || normalized.includes("ui design")) return 2;
  if (normalized.includes("data model") || normalized.includes("schema")) return 3;
  if (normalized.includes("code")) return 4;
  if (normalized.includes("preview") || normalized.includes("render")) return 5;
  return null;
}

const DEVPLAN_TABS: Array<{ key: DevPlanTab; label: string }> = [
  { key: "overview", label: "Overview" },
  { key: "feature", label: "Feature Design" },
  { key: "scenarios", label: "UX Scenarios" },
  { key: "uiDesign", label: "UI Design" },
  { key: "dataModel", label: "Data Model" },
  { key: "code", label: "Code" },
  { key: "preview", label: "Preview" },
];

const IMPROVE_ACTIONS: { mode: ImproveMode; label: string }[] = [
  { mode: "cost", label: "Reduce cost" },
  { mode: "reduce", label: "Reduce scope" },
  { mode: "speed", label: "Speed this up" },
];

const PURCHASE_OFFERS: Record<
  PurchaseTier,
  {
    label: string;
    subtext: string;
    priceText: string;
    oldPriceText?: string;
    amount: number;
    includes: string[];
    description: string;
    isDeposit: boolean;
  }
> = {
  high: {
    label: "Begin Full Build Engagement",
    subtext: "Reserve a build slot with App Robot for end-to-end delivery and launch.",
    priceText: "$999 deposit",
    amount: 999,
    includes: ["End-to-end product build", "Production-ready deployment", "Launch and stabilization support"],
    description: "Start a full engagement where App Robot delivers this product from build through launch.",
    isDeposit: true,
  },
  mid: {
    label: "Start Guided Build",
    subtext: "Work through this plan with our team and ship a production-ready core release.",
    priceText: "$499 deposit",
    amount: 499,
    includes: ["Core production build", "Guided implementation handoff"],
    description: "A guided build path for teams that want to move fast with expert delivery support.",
    isDeposit: true,
  },
  low: {
    label: "Get the Technical Starter Kit",
    subtext: "Download core artifacts and starter code generated from this exact plan.",
    priceText: "Free with signup",
    oldPriceText: "$99",
    amount: 0,
    includes: ["Starter codebase", "Screen/UI artifacts", "Data model and implementation notes"],
    description: "Get the technical starter kit to continue building internally at your own pace.",
    isDeposit: false,
  },
};

const COMPANY_LOGO_URL = `${process.env.PUBLIC_URL || ""}/company-logo.png`;
const COMPANY_NAME = "App Robot, LLC";
const COMPANY_WEBSITE = "https://app-robot.com";
const COMPANY_ADDRESS = "1660 Soldiers Field Rd Ste7 #1010, Boston, MA";
const COMPANY_CONTACT_EMAIL = "support@app-robot.com";

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value.trim() : fallback;
}

function toLines(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => asString(item)).filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
  }
  return [];
}

function firstSentence(text: string): string {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  const parts = normalized.match(/[^.!?]+[.!?]?/g) ?? [normalized];
  return parts[0]?.trim() ?? normalized;
}

function sentenceLimit(text: string, maxSentences: number): string {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  const parts = normalized.match(/[^.!?]+[.!?]?/g) ?? [normalized];
  return parts.slice(0, maxSentences).join(" ").trim();
}

function getTimestampMillis(value: any): number {
  if (value && typeof value.toMillis === "function") return value.toMillis();
  if (typeof value === "number") return value;
  return 0;
}

function formatMaybeJson(value: unknown): string {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return "";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function cleanScenarioLine(line: string): string {
  return line
    .replace(/^[-*]\s*/, "")
    .replace(/^(Scenario|Given|When|Then|And|But)\s*:?\s*/i, "")
    .trim();
}

function improveText(text: string, mode: ImproveMode): string {
  const concise = sentenceLimit(text, 2);
  if (!concise) return text;
  if (mode === "cost") {
    return `${concise} Keep integrations light and reuse standard components to control cost.`;
  }
  if (mode === "reduce") {
    return `${concise} Narrow the first release to the single highest-value workflow.`;
  }
  return `${concise} Prioritize quickest launch path with minimal dependencies and phased polish.`;
}

async function loadImageAsDataUrl(url: string): Promise<string | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise<string | null>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === "string" ? reader.result : null);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export default function DevPlan() {
  const { proposalId } = useParams<{ proposalId: string }>();
  const location = useLocation();
  const { user, isFullySignedIn: canMutate, requireSignedInUser, requireSignedInStatus, authModal } = useMutationAuthGate();
  const confirmedSessionRef = useRef<string | null>(null);

  const [proposalDoc, setProposalDoc] = useState<any | null | undefined>(undefined);
  const [devPlanDoc, setDevPlanDoc] = useState<any | null | undefined>(undefined);
  const [latestJob, setLatestJob] = useState<any | null | undefined>(undefined);
  const [hasActiveJob, setHasActiveJob] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DevPlanTab>("overview");

  const [sectionBusyKey, setSectionBusyKey] = useState<string | null>(null);

  const [editSection, setEditSection] = useState<FounderSectionKey | null>(null);
  const [editValue, setEditValue] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [purchaseEmail, setPurchaseEmail] = useState("");
  const [purchaseNotes, setPurchaseNotes] = useState("");
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState<PurchaseTier>("mid");
  const [purchaseBusy, setPurchaseBusy] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [checkoutConfirming, setCheckoutConfirming] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState<string | null>(null);
  const [checkoutTier, setCheckoutTier] = useState<PurchaseTier | null>(null);
  const [checkoutCancel, setCheckoutCancel] = useState<string | null>(null);
  const [freeDownloadReady, setFreeDownloadReady] = useState(false);
  const [isPreviewRenderComplete, setIsPreviewRenderComplete] = useState(false);

  useEffect(() => {
    ensureAnon().catch((error) => {
      setLoadError((error as Error)?.message || "Could not initialize preview session.");
    });
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [proposalId]);

  useEffect(() => {
    const navState = (location.state as { startBuildPlanError?: string } | null) ?? null;
    const startBuildPlanError = asString(navState?.startBuildPlanError);
    if (startBuildPlanError) {
      setActionError(startBuildPlanError);
      window.history.replaceState({}, document.title, `${location.pathname}${location.search}`);
    }
  }, [location.pathname, location.search, location.state]);

  useEffect(() => {
    if (user?.email) {
      setPurchaseEmail(user.email);
      return;
    }
    const cached = localStorage.getItem("buildEmail");
    if (cached) setPurchaseEmail(cached);
  }, [user?.email]);

  useEffect(() => {
    if (!proposalId || !user) return;
    setLoadError(null);

    const proposalRef = doc(db, "proposals", proposalId);
    const devPlanRef = doc(db, "devPlans", proposalId);
    const jobsRef = query(collection(db, "devPlanJobs"), where("proposalId", "==", proposalId));

    const unsubProposal = onSnapshot(
      proposalRef,
      (snap) => {
        setProposalDoc(snap.exists() ? snap.data() : null);
      },
      (error) => setLoadError((error as Error)?.message || "Could not load proposal details.")
    );

    const unsubDevPlan = onSnapshot(
      devPlanRef,
      (snap) => {
        setDevPlanDoc(snap.exists() ? snap.data() : null);
      },
      (error) => setLoadError((error as Error)?.message || "Could not load dev plan details.")
    );

    const unsubJobs = onSnapshot(
      jobsRef,
      (snap) => {
        const jobs: Array<{ id: string; [key: string]: any }> = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...(docSnap.data() as Record<string, any>),
        }));
        setHasActiveJob(
          jobs.some((job) => {
            const status = asString(job?.status).toLowerCase();
            return status === "pending" || status === "running" || status === "queued";
          })
        );
        const latest =
          jobs.sort((a, b) => getTimestampMillis(b.createdAt) - getTimestampMillis(a.createdAt))[0] ?? null;
        setLatestJob(latest);
      },
      (error) => setLoadError((error as Error)?.message || "Could not load generation job status.")
    );

    return () => {
      unsubProposal();
      unsubDevPlan();
      unsubJobs();
    };
  }, [proposalId, user]);

  const isJobInProgress = useMemo(() => {
    const status = asString(latestJob?.status).toLowerCase();
    return hasActiveJob || status === "pending" || status === "running" || status === "queued";
  }, [hasActiveJob, latestJob]);

  const featureText = asString(devPlanDoc?.feature) || asString(devPlanDoc?.featureDesign);
  const scenarioText = asString(devPlanDoc?.scenario) || asString(devPlanDoc?.uxScenario);
  const uiDesignText = asString(devPlanDoc?.uiDesign);
  const dataModelText = asString(devPlanDoc?.dataModel);
  const uiCodeText = asString(devPlanDoc?.uiCode);

  useEffect(() => {
    if (!uiCodeText) {
      setIsPreviewRenderComplete(false);
      return;
    }
    setIsPreviewRenderComplete(false);
    const timer = window.setTimeout(() => {
      setIsPreviewRenderComplete(true);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [proposalId, uiCodeText]);

  const output = proposalDoc?.output || {};
  const keyFeatures: { title: string; desc: string; priority: string }[] = Array.isArray(output?.keyFeatures)
    ? output.keyFeatures
        .map((feature: any) => ({
          title: asString(feature?.title),
          desc: asString(feature?.desc),
          priority: asString(feature?.priority) || "MVP",
        }))
        .filter((feature: any) => feature.title || feature.desc)
    : [];
  const focusFeature = keyFeatures.find((feature) => feature.priority === "MVP") || keyFeatures[0];
  const focusAction = focusFeature?.title || "the highest-value user action";

  const deliveryPlan: { phase: string; weeks: number }[] = Array.isArray(output?.deliveryPlan)
    ? output.deliveryPlan.map((phase: any, idx: number) => ({
        phase: asString(phase?.phase, `Phase ${idx + 1}`),
        weeks: Math.max(1, Number(phase?.weeks) || 1),
      }))
    : [];
  const timelineWeeks = Math.max(
    6,
    deliveryPlan.reduce((sum, phase) => sum + phase.weeks, 0) || 6
  );
  const pricingAssessment = useMemo(
    () =>
      buildPricingAssessment({
        idea: proposalDoc?.inputs?.idea,
        inputs: proposalDoc?.inputs,
        output,
      }),
    [output, proposalDoc?.inputs]
  );
  const investmentRange = pricingAssessment.recommendedRange.label;

  const founderSummary =
    asString(devPlanDoc?.founderSummary) ||
    sentenceLimit(
      asString(output?.executiveSummary) ||
        asString(output?.solutionOverview) ||
        "App Robot translated your concept into a practical build plan focused on speed, clarity, and execution confidence.",
      2
    );

  const mvpScopeNotes = asString(devPlanDoc?.mvpScopeNotes);
  const mvpScopeBullets = mvpScopeNotes
    ? toLines(mvpScopeNotes).slice(0, 5)
    : [
        "Home Screen: dashboard, priorities, and simple navigation.",
        `Focus Feature Screen: users complete ${focusAction.toLowerCase()} with a clear end-to-end flow.`,
        "User Profile Screen: account details, saved history, and personal settings.",
        ...keyFeatures
          .filter((feature) => feature.priority === "MVP")
          .slice(0, 2)
          .map((feature) => `${feature.title}: ${firstSentence(feature.desc) || "Core must-have capability."}`),
      ].slice(0, 5);

  const timelineNotes = asString(devPlanDoc?.timelineNotes);
  const investmentNotes = asString(devPlanDoc?.investmentNotes);
  const experienceSummary = asString(devPlanDoc?.experienceSummary);
  const screensSummary = asString(devPlanDoc?.screensSummary);
  const technicalSummary = asString(devPlanDoc?.technicalSummary);

  const userExperienceLines = experienceSummary
    ? toLines(experienceSummary).slice(0, 4)
    : scenarioText
        .split("\n")
        .map(cleanScenarioLine)
        .filter(Boolean)
        .slice(0, 4);

  const defaultExperienceLines = userExperienceLines.length
    ? userExperienceLines
    : [
        "Users land on a clear home view and understand what to do first.",
        "They complete the highest-value action quickly through the Focus Feature screen.",
        "They manage preferences and saved history inside a personal profile area.",
      ];

  const screensNoteLines = screensSummary ? toLines(screensSummary) : [];

  const hasFeatureDesign = Boolean(featureText);
  const hasUxScenarios = Boolean(scenarioText);
  const hasUiDesign = Boolean(uiDesignText);
  const hasDataModel = Boolean(dataModelText);
  const hasGeneratedCode = Boolean(uiCodeText);
  const latestJobStatus = asString(latestJob?.status).toLowerCase();
  const latestJobStep = asString(latestJob?.currentStep || latestJob?.step);

  const rawUiCodeScreens = useMemo(() => {
    if (!uiCodeText) return [] as { key: string; code: string }[];
    try {
      const parsed = JSON.parse(uiCodeText);
      if (parsed && typeof parsed === "object") {
        return Object.keys(parsed).map((key) => ({
          key,
          code: formatMaybeJson((parsed as any)[key]),
        }));
      }
    } catch {
      // fall back to plain text below
    }
    return [{ key: "screen", code: uiCodeText }];
  }, [uiCodeText]);

  const snackPreviewScreens = useMemo(() => {
    if (!uiCodeText) return [] as { key: string; code: string }[];
    const normalizeCode = (value: unknown): string => {
      if (typeof value === "string") return value.trim();
      return formatMaybeJson(value).trim();
    };
    try {
      const parsed = JSON.parse(uiCodeText);
      if (parsed && typeof parsed === "object") {
        return Object.entries(parsed as Record<string, unknown>)
          .map(([key, code]) => ({
            key,
            code: normalizeCode(code),
          }))
          .filter((entry) => Boolean(entry.code));
      }
    } catch {
      // Continue with plain-text fallback when uiCode is not JSON.
    }
    return [{ key: "screen", code: uiCodeText }];
  }, [uiCodeText]);

  const hasAnyTechnicalContent =
    hasFeatureDesign || hasUxScenarios || hasUiDesign || hasDataModel || hasGeneratedCode;
  const hasCompleteTechnicalPlan =
    hasFeatureDesign && hasUxScenarios && hasUiDesign && hasDataModel && hasGeneratedCode;
  const hasJobError = latestJobStatus === "error";
  const stageCompletion = [
    hasFeatureDesign,
    hasUxScenarios,
    hasUiDesign,
    hasDataModel,
    hasGeneratedCode,
    isPreviewRenderComplete,
  ];
  const firstPendingStageIndex = stageCompletion.findIndex((isDone) => !isDone);
  const stageFromArtifacts =
    firstPendingStageIndex === -1 ? DEVPLAN_LOADING_STAGES.length - 1 : firstPendingStageIndex;
  const stageFromJobStep = getLoadingStageIndexFromJobStep(latestJobStep);
  const resolvedLoadingStageIndex = Math.max(stageFromArtifacts, stageFromJobStep ?? 0);
  const currentLoadingStep = latestJobStep || DEVPLAN_LOADING_STAGES[resolvedLoadingStageIndex];
  const areBuildProcessesComplete = hasCompleteTechnicalPlan && isPreviewRenderComplete;
  const latestJobFailure = useMemo(() => {
    const reason = asString(latestJob?.error, "Build plan generation failed.");
    const failedStep = asString(latestJob?.failedStep || latestJob?.step);
    const sourceCode = asString(latestJob?.errorCode || latestJob?.sourceCode);
    const errorId = asString(latestJob?.errorId);
    const extras: string[] = [];
    if (failedStep) extras.push(`step: ${failedStep}`);
    if (sourceCode) extras.push(`cause: ${sourceCode}`);
    if (errorId) extras.push(`ref: ${errorId}`);
    return extras.length ? `${reason} (${extras.join(" • ")})` : reason;
  }, [latestJob]);
  const isSnapshotLoading = proposalDoc === undefined || devPlanDoc === undefined || latestJob === undefined;
  const showGeneratingState =
    !isSnapshotLoading &&
    !hasJobError &&
    !areBuildProcessesComplete &&
    (isJobInProgress || hasAnyTechnicalContent);

  const mutationSectionMessage = "Create your account to edit, regenerate, and save this build plan.";
  const requireMutation = (action: () => void | Promise<void>, message = mutationSectionMessage) =>
    requireSignedInUser(action, message);

  useEffect(() => {
    if (activeTab !== "preview" || !snackPreviewScreens.length) return;

    const scriptId = "devplan-snack-embed-js";
    const initializeSnack = () => {
      window.ExpoSnack?.initialize?.();
    };
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      initializeSnack();
      return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.async = true;
    script.src = "https://snack.expo.dev/embed.js";
    script.onload = initializeSnack;
    document.body.appendChild(script);

    return () => {
      script.onload = null;
    };
  }, [activeTab, snackPreviewScreens]);

  useEffect(() => {
    if (!proposalId) return;
    const params = new URLSearchParams(location.search);
    const checkoutStatus = params.get("checkout");
    const sessionId = params.get("session_id");
    const tierParam = params.get("tier");
    const urlTier: PurchaseTier | null =
      tierParam === "high" || tierParam === "mid" || tierParam === "low" ? tierParam : null;

    if (checkoutStatus === "canceled") {
      setCheckoutCancel("Checkout canceled. You can choose any option when you are ready.");
      setCheckoutSuccess(null);
      setPurchaseError(null);
      setCheckoutTier(urlTier);
      window.history.replaceState({}, document.title, `/dev-plan/${proposalId}`);
      return;
    }

    if (checkoutStatus !== "success" || !sessionId) return;
    if (confirmedSessionRef.current === sessionId) return;
    confirmedSessionRef.current = sessionId;
    setCheckoutConfirming(true);
    setCheckoutCancel(null);
    setPurchaseError(null);

    confirmCheckoutSessionServer({ sessionId, proposalId })
      .then((result: any) => {
        const data = result || {};
        if (data?.paid) {
          const paidTier: PurchaseTier =
            data?.tier === "high" || data?.tier === "mid" || data?.tier === "low" ? data.tier : "low";
          setCheckoutTier(paidTier);
          setCheckoutSuccess(
            asString(data?.message) ||
              (paidTier === "high"
                ? "Deposit received. We'll reach out within 24 hours to kick off your build and support plan."
                : paidTier === "mid"
                  ? "Deposit received. We'll reach out within 24 hours to kick off your app build."
                  : "Payment received. Your build kit is ready.")
          );
          emitPurchaseTracking(analytics, {
            proposalId,
            tier: paidTier,
            transactionId: purchaseTransactionIdFromSession(sessionId),
            amount: PURCHASE_OFFERS[paidTier].amount,
          });
        } else {
          setPurchaseError("Your payment is not confirmed yet. Please refresh in a moment.");
        }
      })
      .catch((error) => {
        setPurchaseError(formatFunctionActionError(error, "Could not confirm payment right now."));
      })
      .finally(() => {
        setCheckoutConfirming(false);
        window.history.replaceState({}, document.title, `/dev-plan/${proposalId}`);
      });
  }, [location.search, proposalId]);

  function sectionValue(section: FounderSectionKey): string {
    switch (section) {
      case "founderSummary":
        return founderSummary;
      case "mvpScopeNotes":
        return mvpScopeBullets.join("\n");
      case "timelineNotes":
        return timelineNotes || "Keep timeline lean and aligned to the 3-screen MVP.";
      case "investmentNotes":
        return (
          investmentNotes ||
          "Treat this as a realistic estimate based on scope. Final cost depends on integrations and level of polish."
        );
      case "experienceSummary":
        return defaultExperienceLines.join("\n");
      case "screensSummary":
        return screensNoteLines.join("\n");
      case "technicalSummary":
        return (
          technicalSummary ||
          "We build with a reliable React Native + Firebase stack, phased delivery checkpoints, and launch instrumentation."
        );
      default:
        return "";
    }
  }

  function openEdit(section: FounderSectionKey) {
    if (analytics) logEvent(analytics, "devplan_edit_section_click", { section, proposalId });
    setActionError(null);
    setEditError(null);
    setEditSection(section);
    setEditValue(sectionValue(section));
  }

  async function saveEdit() {
    if (!proposalId || !editSection) return;
    setEditSaving(true);
    setEditError(null);
    try {
      await updateDevPlanFieldsServer(proposalId, { [editSection]: editValue.trim() });
      setEditSection(null);
      if (analytics) logEvent(analytics, "devplan_edit_section_saved", { section: editSection, proposalId });
    } catch (error) {
      setEditError(formatFunctionActionError(error, "Could not save this section right now."));
    } finally {
      setEditSaving(false);
    }
  }

  async function improveSection(section: FounderSectionKey, mode: ImproveMode) {
    if (!proposalId) return;
    const busyKey = `${section}:${mode}`;
    setSectionBusyKey(busyKey);
    setActionError(null);
    try {
      const improved = improveText(sectionValue(section), mode);
      await updateDevPlanFieldsServer(proposalId, { [section]: improved });
      if (analytics) logEvent(analytics, "devplan_improve_section_click", { section, mode, proposalId });
    } catch (error) {
      setActionError(formatFunctionActionError(error, "Could not improve this section right now."));
    } finally {
      setSectionBusyKey(null);
    }
  }

  async function regenerateSection(target: RegenerateTarget) {
    if (!proposalId) {
      setActionError("Missing proposal reference.");
      return;
    }
    const busyKey = `regen:${target}`;
    setSectionBusyKey(busyKey);
    setActionError(null);
    try {
      let patch: Record<string, any> = {};

      if (target === "feature") {
        const result = await generateFeatureDesignServer({ proposalId, output: output || {} });
        patch = { feature: formatMaybeJson(result) };
      } else if (target === "scenario") {
        const seedFeature =
          featureText || formatMaybeJson(await generateFeatureDesignServer({ proposalId, output: output || {} }));
        const result = await generateUXScenarioServer({ proposalId, feature: seedFeature });
        patch = { feature: seedFeature, scenario: formatMaybeJson(result) };
      } else if (target === "uiDesign") {
        const seedFeature =
          featureText || formatMaybeJson(await generateFeatureDesignServer({ proposalId, output: output || {} }));
        const seedScenario =
          scenarioText || formatMaybeJson(await generateUXScenarioServer({ proposalId, feature: seedFeature }));
        const result = await generateUIDesignServer({ proposalId, feature: seedFeature, scenario: seedScenario });
        patch = {
          feature: seedFeature,
          scenario: seedScenario,
          uiDesign: formatMaybeJson(result),
        };
      } else if (target === "dataModel") {
        const seedFeature =
          featureText || formatMaybeJson(await generateFeatureDesignServer({ proposalId, output: output || {} }));
        const seedScenario =
          scenarioText || formatMaybeJson(await generateUXScenarioServer({ proposalId, feature: seedFeature }));
        const seedUiDesign =
          uiDesignText ||
          formatMaybeJson(
            await generateUIDesignServer({ proposalId, feature: seedFeature, scenario: seedScenario })
          );
        const result = await generateDataModelServer({
          proposalId,
          feature: seedFeature,
          scenario: seedScenario,
          uiDesign: seedUiDesign,
        });
        patch = {
          feature: seedFeature,
          scenario: seedScenario,
          uiDesign: seedUiDesign,
          dataModel: formatMaybeJson(result),
        };
      }

      await updateDevPlanFieldsServer(proposalId, patch);
      if (analytics) logEvent(analytics, "devplan_partial_regenerate_click", { target, proposalId });
    } catch (error) {
      setActionError(formatFunctionActionError(error, "Could not regenerate this section right now."));
    } finally {
      setSectionBusyKey(null);
    }
  }

  async function startCheckoutCore() {
    if (!proposalId) {
      setPurchaseError("Missing proposal reference.");
      return;
    }
    if (selectedTier === "low") {
      setPurchaseError("Use the free signup flow to unlock your download.");
      return;
    }

    const email = purchaseEmail.trim();
    if (!email.match(/^\S+@\S+\.\S+$/)) {
      setPurchaseError("Please enter a valid email.");
      return;
    }

    if ((selectedTier === "high" || selectedTier === "mid") && purchaseNotes.trim().length > 1200) {
      setPurchaseError("Notes are too long. Please keep notes under 1200 characters.");
      return;
    }

    setPurchaseBusy(true);
    setPurchaseError(null);
    try {
      const result = await createCheckoutSessionServer({
        proposalId,
        tier: selectedTier,
        email,
        notes: purchaseNotes.trim() || null,
        attribution: getStoredAttributionPayload(),
        summary: {
          mvpScope: mvpScopeBullets.slice(0, 5),
          timelineWeeks,
          investmentRange,
        },
      });

      const checkoutUrl = asString(result?.url);
      if (!checkoutUrl) throw new Error("Could not start checkout.");

      localStorage.setItem("buildEmail", email);
      emitCheckoutBeginTracking(analytics, {
        proposalId,
        tier: selectedTier,
        amount: PURCHASE_OFFERS[selectedTier].amount,
      });
      window.location.assign(checkoutUrl);
    } catch (error) {
      setPurchaseError(formatFunctionActionError(error, "Could not start payment. Please retry."));
      setPurchaseBusy(false);
    }
  }

  function openPurchaseModal(tier: PurchaseTier) {
    setSelectedTier(tier);
    setPurchaseError(null);
    setCheckoutSuccess(null);
    setCheckoutCancel(null);
    setFreeDownloadReady(false);
    if (tier === "low") {
      setPurchaseNotes("");
    }
    setShowPurchaseModal(true);
    if (analytics) {
      logEvent(analytics, "devplan_purchase_modal_open", { proposalId, tier });
    }
  }

  async function createDownloadPackage(email: string) {
    if (!proposalId) return;
    const proposalOutput = (proposalDoc?.output as Record<string, any> | undefined) || {};
    const proposalIdea = asString(proposalDoc?.inputs?.idea);
    const keyFeaturesLines = Array.isArray(proposalOutput?.keyFeatures)
      ? proposalOutput.keyFeatures
          .map((feature: any) => {
            const priority = asString(feature?.priority, "MVP");
            const title = asString(feature?.title);
            const desc = asString(feature?.desc);
            return [priority, title && `${title}:`, desc].filter(Boolean).join(" ");
          })
          .filter(Boolean)
      : [];
    const deliveryPlanLines = Array.isArray(proposalOutput?.deliveryPlan)
      ? proposalOutput.deliveryPlan
          .map((phase: any, idx: number) => {
            const phaseName = asString(phase?.phase, `Phase ${idx + 1}`);
            const weeks = Number(phase?.weeks) > 0 ? Number(phase.weeks) : 0;
            const outcomes = toLines(phase?.outcomes).join("; ");
            const weekLabel = weeks ? `${weeks} week${weeks === 1 ? "" : "s"}` : "";
            return [phaseName, weekLabel && `(${weekLabel})`, outcomes && `- ${outcomes}`].filter(Boolean).join(" ");
          })
          .filter(Boolean)
      : [];
    const pricingLines = Array.isArray(proposalOutput?.pricingPackages)
      ? proposalOutput.pricingPackages
          .map((pkg: any, idx: number) => {
            const name = asString(pkg?.name, `Package ${idx + 1}`);
            const price = Number(pkg?.price);
            const scope = toLines(pkg?.scope).join("; ");
            const priceLabel = Number.isFinite(price) && price > 0 ? formatUsd(Math.round(price)) : "TBD";
            return `${name} - ${priceLabel}${scope ? ` - ${scope}` : ""}`;
          })
          .filter(Boolean)
      : [];

    const logoDataUrl = await loadImageAsDataUrl(COMPANY_LOGO_URL);
    const copyrightYear = new Date().getFullYear();
    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 44;
    const headerHeight = 78;
    const footerHeight = 28;
    const contentTop = margin + headerHeight;
    const contentBottom = pageHeight - margin - footerHeight;
    const contentWidth = pageWidth - margin * 2;
    let y = contentTop;

    const drawHeader = () => {
      const logoSize = 44;
      const headerY = margin - 4;
      let contentX = margin;

      if (logoDataUrl) {
        try {
          pdf.addImage(logoDataUrl, "PNG", margin, headerY, logoSize, logoSize);
          contentX = margin + logoSize + 10;
        } catch {
          contentX = margin;
        }
      }

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.text(COMPANY_NAME, contentX, headerY + 12);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.text(COMPANY_WEBSITE, contentX, headerY + 25);
      pdf.text(COMPANY_ADDRESS, contentX, headerY + 37);
      pdf.text(COMPANY_CONTACT_EMAIL, contentX, headerY + 49);

      const dividerY = margin + headerHeight - 14;
      pdf.setDrawColor(210);
      pdf.line(margin, dividerY, pageWidth - margin, dividerY);
    };

    const drawFooter = () => {
      const dividerY = pageHeight - margin - footerHeight + 4;
      pdf.setDrawColor(210);
      pdf.line(margin, dividerY, pageWidth - margin, dividerY);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.text(`© ${copyrightYear} ${COMPANY_NAME}. All rights reserved.`, pageWidth / 2, dividerY + 14, {
        align: "center",
      });
    };

    const ensureSpace = (needed = 16) => {
      if (y + needed > contentBottom) {
        pdf.addPage();
        drawHeader();
        drawFooter();
        y = contentTop;
      }
    };

    const writeHeading = (text: string) => {
      ensureSpace(28);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(14);
      pdf.text(text, margin, y);
      y += 20;
    };

    const writeParagraph = (text: string, indent = 0) => {
      const normalized = asString(text);
      if (!normalized) return;
      const x = margin + indent;
      const lines = pdf.splitTextToSize(normalized, contentWidth - indent);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);
      lines.forEach((line: string) => {
        ensureSpace(16);
        pdf.text(line, x, y);
        y += 14;
      });
      y += 4;
    };

    const writeBullets = (items: string[]) => {
      items.filter(Boolean).forEach((item) => {
        ensureSpace(16);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(11);
        pdf.text("•", margin, y);
        const lines = pdf.splitTextToSize(item, contentWidth - 14);
        lines.forEach((line: string, index: number) => {
          ensureSpace(16);
          pdf.text(line, margin + 12, y);
          if (index < lines.length - 1) y += 14;
        });
        y += 18;
      });
      y += 2;
    };

    const writeTextSection = (title: string, value: string) => {
      if (!asString(value)) return;
      writeHeading(title);
      writeParagraph(value);
    };

    drawHeader();
    drawFooter();

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.text("App Robot Proposal + Build Plan", margin, y);
    y += 24;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.text(`Proposal ID: ${proposalId}`, margin, y);
    y += 14;
    pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, y);
    y += 14;
    pdf.text(`Downloaded by: ${email}`, margin, y);
    y += 20;

    writeHeading("Proposal");
    writeTextSection("Idea", proposalIdea);
    writeTextSection("Executive Summary", asString(proposalOutput?.executiveSummary));
    writeTextSection("Problem Statement", asString(proposalOutput?.problemStatement));
    writeTextSection("Solution Overview", asString(proposalOutput?.solutionOverview));
    if (keyFeaturesLines.length) {
      writeHeading("Key Features");
      writeBullets(keyFeaturesLines);
    }
    if (deliveryPlanLines.length) {
      writeHeading("Delivery Plan");
      writeBullets(deliveryPlanLines);
    }
    if (pricingLines.length) {
      writeHeading("Pricing Packages");
      writeBullets(pricingLines);
    }
    const riskLines = toLines(proposalOutput?.risksMitigations);
    if (riskLines.length) {
      writeHeading("Risks & Mitigations");
      writeBullets(riskLines);
    }
    const acceptanceLines = toLines(proposalOutput?.acceptanceCriteria);
    if (acceptanceLines.length) {
      writeHeading("Acceptance Criteria");
      writeBullets(acceptanceLines);
    }

    writeHeading("Build Plan");
    writeTextSection("Overview", founderSummary);
    if (mvpScopeBullets.length) {
      writeHeading("MVP Scope");
      writeBullets(mvpScopeBullets.slice(0, 8));
    }
    writeTextSection("Timeline", `${timelineWeeks} weeks. ${timelineNotes || ""}`.trim());
    writeTextSection(
      "Investment Range (Planning)",
      `${investmentRange}. ${investmentNotes || `${pricingAssessment.rangeExplanation} ${pricingAssessment.riskNote}`}`.trim()
    );
    writeTextSection("Feature Design", featureText);
    writeTextSection("UX Scenarios", scenarioText);
    writeTextSection("UI Design", uiDesignText);
    writeTextSection("Data Model", dataModelText);
    writeTextSection("Generated UI Code", uiCodeText);

    pdf.save(`app-robot-proposal-build-plan-${proposalId}.pdf`);
  }

  async function persistFreeDownloadClaim(email: string, authProvider: "email_password" | "google") {
    if (!proposalId) throw new Error("Missing proposal reference.");
    await registerFreeDownloadServer({
      proposalId,
      email,
      authProvider,
      attribution: getStoredAttributionPayload(),
      summary: {
        mvpScope: mvpScopeBullets.slice(0, 5),
        timelineWeeks,
        investmentRange,
      },
    });
  }

  async function completeFreeDownload(email: string, authProvider: "email_password" | "google") {
    if (!proposalId) throw new Error("Missing proposal reference.");
    await persistFreeDownloadClaim(email, authProvider);
    localStorage.setItem("buildEmail", email);
    await createDownloadPackage(email);
    setCheckoutTier("low");
    setFreeDownloadReady(true);
    setCheckoutSuccess("Free download unlocked. Your package has started downloading.");
    setPurchaseError(null);
    setShowPurchaseModal(false);
    emitSignupLeadTracking(analytics, { proposalId, authProvider });
  }

  async function claimFreeDownload() {
    const email = purchaseEmail.trim().toLowerCase();
    if (!email.match(/^\S+@\S+\.\S+$/)) {
      setPurchaseError("Please enter a valid email.");
      return;
    }
    setPurchaseBusy(true);
    setPurchaseError(null);
    try {
      await completeFreeDownload(email, "email_password");
    } catch (error) {
      setPurchaseError(formatFunctionActionError(error, "Could not complete free download right now."));
    } finally {
      setPurchaseBusy(false);
    }
  }

  async function startCheckout() {
    await startCheckoutCore();
  }

  const selectedOffer = PURCHASE_OFFERS[selectedTier];

  const sectionActions = (section: FounderSectionKey, regenerateTarget?: RegenerateTarget) => (
    <div className="devplan-section-actions">
      <button
        type="button"
        className="proposal-link-btn"
        onClick={() => {
          void requireMutation(
            () => openEdit(section),
            "Create your account to edit and save build plan changes."
          );
        }}
      >
        Edit
      </button>
      {IMPROVE_ACTIONS.map((action) => {
        const busyKey = `${section}:${action.mode}`;
        return (
          <button
            key={busyKey}
            type="button"
            className="proposal-chip-btn"
            onClick={() => {
              void requireMutation(
                () => improveSection(section, action.mode),
                "Sign up to improve and save this section."
              );
            }}
            disabled={sectionBusyKey === busyKey}
          >
            {sectionBusyKey === busyKey ? "Improving…" : action.label}
          </button>
        );
      })}
      {regenerateTarget && (
        <button
          type="button"
          className="proposal-chip-btn"
          onClick={() => {
            void requireMutation(
              () => regenerateSection(regenerateTarget),
              "Sign up to regenerate this section."
            );
          }}
          disabled={sectionBusyKey === `regen:${regenerateTarget}`}
        >
          {sectionBusyKey === `regen:${regenerateTarget}` ? "Regenerating…" : "Regenerate section"}
        </button>
      )}
    </div>
  );

  return (
    <>
      <div className="container devplan-page">
        <header className="topbar proposal-topbar">
          <Link to={proposalId ? `/p/${proposalId}` : "/"} className="proposal-back-link">← Back to proposal</Link>
          <h2 className="proposal-title">App Robot Build Plan</h2>
          <span className="proposal-topbar-spacer" />
        </header>

        {loadError && <div className="error">{loadError}</div>}
        {actionError && <div className="error">{actionError}</div>}
        {!canMutate && (
          <section className="proposal-loading-card">
            <h3>Read-only preview</h3>
            <p>Sign up to edit and save section changes. Full build-plan generation is still available in preview mode.</p>
          </section>
        )}

        {isSnapshotLoading && (
          <section className="proposal-loading-card">
            <h3>Loading your build plan…</h3>
            <p>Syncing proposal, build artifacts, and generation status.</p>
          </section>
        )}

        {showGeneratingState && (
          <section className="proposal-loading-card">
            <h3>Building your implementation plan</h3>
            <ul>
              {DEVPLAN_LOADING_STAGES.map((stage, index) => (
                <li key={stage} className={index <= resolvedLoadingStageIndex ? "is-active" : ""}>
                  {stage}
                </li>
              ))}
            </ul>
            <p>App Robot is generating execution artifacts from your approved proposal.</p>
            {currentLoadingStep && (
              <p className="devplan-muted-line">Current step: {currentLoadingStep}</p>
            )}
          </section>
        )}

        {!showGeneratingState && !hasAnyTechnicalContent && !hasJobError && !isSnapshotLoading && (
          <section className="proposal-loading-card">
            <h3>Preparing your build plan</h3>
            <ul>
              {DEVPLAN_LOADING_STAGES.map((stage, index) => (
                <li key={stage} className={index === 0 ? "is-active" : ""}>
                  {stage}
                </li>
              ))}
            </ul>
            <p>Generation begins when you click "View Build Plan" from the proposal page.</p>
          </section>
        )}

        {hasJobError && !isJobInProgress && (
          <section className="proposal-section-card devplan-failure-card">
            <h3>Generation needs a retry</h3>
            <p>{latestJobFailure}</p>
            <p>Go back to the proposal page and click "View Build Plan" to start generation again.</p>
          </section>
        )}

        <section className="proposal-section-card devplan-tabs-shell">
          <div className="devplan-tabs" role="tablist" aria-label="Build plan sections">
            {DEVPLAN_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                className={`devplan-tab-btn ${activeTab === tab.key ? "is-active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </section>

        <article className="proposal decision-proposal devplan-decision-article">
          {activeTab === "overview" && (
            <section className="proposal-section-card">
              <div className="proposal-section-header">
                <h3>Overview</h3>
                {sectionActions("founderSummary")}
              </div>
              <p className="devplan-muted-line">{founderSummary}</p>
              <div className="devplan-overview-grid">
                <div className="devplan-overview-card">
                  <h4>MVP Scope</h4>
                  <ul>
                    {mvpScopeBullets.slice(0, 3).map((bullet, index) => (
                      <li key={`scope-${index}`}>{bullet}</li>
                    ))}
                  </ul>
                </div>
                <div className="devplan-overview-card">
                  <h4>Timeline</h4>
                  <p>{timelineWeeks} weeks</p>
                  <small>{timelineNotes || "Week 1-2 foundation, Week 3-4 core flow, Week 5-6 polish + launch."}</small>
                </div>
                <div className="devplan-overview-card">
                  <h4>Investment range</h4>
                  <p>{investmentRange}</p>
                  <small>
                    {investmentNotes ||
                      "Planning estimate based on scope, integration depth, quality bar, and delivery shape."}
                  </small>
                </div>
                <div className="devplan-overview-card">
                  <h4>Build approach</h4>
                  <p className="devplan-approach-copy">
                    {technicalSummary ||
                      "Phased React Native + Firebase delivery with stable checkpoints, QA, and launch instrumentation."}
                  </p>
                </div>
              </div>
            </section>
          )}

          {activeTab === "feature" && (
            <section className="proposal-section-card">
              <div className="proposal-section-header">
                <h3>Feature Design</h3>
                {sectionActions("mvpScopeNotes", "feature")}
              </div>
              <p className="devplan-muted-line">
                Implementation-level breakdown of the core capabilities and behaviors to build.
              </p>
              <pre className="devplan-code-block">
                {featureText || "Feature design is being generated. Regenerate if this section stays empty."}
              </pre>
            </section>
          )}

          {activeTab === "scenarios" && (
            <section className="proposal-section-card">
              <div className="proposal-section-header">
                <h3>UX Scenarios</h3>
                {sectionActions("experienceSummary", "scenario")}
              </div>
              <p className="devplan-muted-line">User flows and interaction sequences that guide implementation scope.</p>
              <ul>
                {defaultExperienceLines.map((line, index) => (
                  <li key={`experience-${index}`}>{line}</li>
                ))}
              </ul>
              <h4 className="devplan-subheading">Generated scenario source</h4>
              <pre className="devplan-code-block">{scenarioText || "UX scenarios are being generated."}</pre>
            </section>
          )}

          {activeTab === "uiDesign" && (
            <section className="proposal-section-card">
              <div className="proposal-section-header">
                <h3>UI Design</h3>
                {sectionActions("screensSummary", "uiDesign")}
              </div>
              <div className="devplan-screen-grid">
                <div className="devplan-screen-card">
                  <h4>Home Screen</h4>
                  <p><strong>Purpose:</strong> orient users and show top priorities.</p>
                  <p><strong>User can:</strong> see status, navigate key actions, and start quickly.</p>
                </div>
                <div className="devplan-screen-card">
                  <h4>Focus Feature Screen</h4>
                  <p><strong>Purpose:</strong> deliver the app&apos;s highest-value workflow.</p>
                  <p><strong>User can:</strong> complete <strong>{focusAction}</strong> end-to-end.</p>
                </div>
                <div className="devplan-screen-card">
                  <h4>User Profile Screen</h4>
                  <p><strong>Purpose:</strong> manage user-specific settings and history.</p>
                  <p><strong>User can:</strong> update account details and review saved data.</p>
                </div>
              </div>
              {!!screensNoteLines.length && (
                <>
                  <h4 className="devplan-subheading">Screen notes</h4>
                  <ul>
                    {screensNoteLines.map((item, index) => (
                      <li key={`screen-note-${index}`}>{item}</li>
                    ))}
                  </ul>
                </>
              )}
              <h4 className="devplan-subheading">Generated UI design source</h4>
              <pre className="devplan-code-block">{uiDesignText || "UI design details are being generated."}</pre>
            </section>
          )}

          {activeTab === "dataModel" && (
            <section className="proposal-section-card">
              <div className="proposal-section-header">
                <h3>Generated Data Model</h3>
                {sectionActions("technicalSummary", "dataModel")}
              </div>
              <p className="devplan-muted-line">Entity schema, relationships, and shape definitions for implementation.</p>
              <pre className="devplan-code-block">{dataModelText || "Data model is being generated."}</pre>
            </section>
          )}

          {activeTab === "code" && (
            <section className="proposal-section-card">
              <div className="proposal-section-header">
                <h3>Generated UI Code</h3>
              </div>
              <p className="devplan-muted-line">Draft screen code generated from the current feature and UI model.</p>
              {rawUiCodeScreens.length ? (
                <div className="devplan-code-grid">
                  {rawUiCodeScreens.map((screen) => (
                    <div key={screen.key} className="devplan-code-card">
                      <h4>{screen.key}</h4>
                      <pre className="devplan-code-block">{screen.code}</pre>
                    </div>
                  ))}
                </div>
              ) : (
                <pre className="devplan-code-block">Generated UI code will appear here once ready.</pre>
              )}
            </section>
          )}

          {activeTab === "preview" && (
            <section className="proposal-section-card">
              <div className="proposal-section-header">
                <h3>Interactive Preview</h3>
              </div>
              <p className="devplan-muted-line">Expo Snack previews generated from the UI code output.</p>
              {!!snackPreviewScreens.length ? (
                <div className="devplan-snack-grid">
                  {snackPreviewScreens.slice(0, 3).map((screen, index) => (
                    <div key={`snack-${screen.key}-${index}`} className="devplan-snack-card">
                      <div className="devplan-snack-header">
                        <h4>{screen.key || `Screen ${index + 1}`}</h4>
                        <a
                          href={`https://snack.expo.dev/?code=${encodeURIComponent(screen.code)}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open in Snack
                        </a>
                      </div>
                      <div className="devplan-snack-frame">
                        <div
                          data-snack-code={encodeURIComponent(screen.code)}
                          data-snack-preview="true"
                          data-snack-platform="web"
                          data-snack-theme="dark"
                          data-snack-supported-platforms="web"
                          className="devplan-snack-canvas"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="devplan-empty-preview">
                  Generated UI code is not ready yet. Regenerate the plan to refresh this tab.
                </div>
              )}
            </section>
          )}
        </article>

        {areBuildProcessesComplete && (
          <section className="proposal-final-cta devplan-commit-cta">
            <h3>Choose your next step</h3>
            <p className="devplan-purchase-intro">
              This is the fastest path to go from plan to working product.
            </p>
            <p className="devplan-muted-line">
              Start with a small commitment and scale up as you move forward.
            </p>
            <p className="devplan-muted-line">
              Investment range ({investmentRange}) reflects likely full project cost based on this plan.
            </p>
            <p className="devplan-muted-line">
              The options below are next-step engagement offers (deposits or free starter kit), not the full project total.
            </p>
            <p className="devplan-muted-line">
              MVP scope: {mvpScopeBullets[0]}<br />
              Timeline: {timelineWeeks} weeks
            </p>
            {checkoutCancel && <div className="error">{checkoutCancel}</div>}
            {purchaseError && !showPurchaseModal && <div className="error">{purchaseError}</div>}
            {checkoutSuccess && (
              <div className="devplan-success">
                {checkoutSuccess}
                {checkoutTier === "low" && (
                  <div className="devplan-success-note">
                    {freeDownloadReady ?
                      "Your build package download has started." :
                      "Your build kit purchase is confirmed."}
                  </div>
                )}
              </div>
            )}
            {checkoutConfirming && (
              <div className="proposal-loading-card">
                <h3>Confirming your payment…</h3>
                <p>Please wait while App Robot verifies your Stripe Checkout session.</p>
              </div>
            )}
            <div className="devplan-offer-grid">
              {(["high", "mid", "low"] as PurchaseTier[]).map((tier) => {
                const offer = PURCHASE_OFFERS[tier];
                const cardClass =
                  tier === "mid" ? "is-featured" : tier === "high" ? "is-secondary" : "is-tertiary";
                return (
                  <article key={tier} className={`devplan-offer-card ${cardClass}`}>
                    <h4>{offer.label}</h4>
                    <p className="devplan-offer-subtext">{offer.subtext}</p>
                    {tier === "low" ? (
                      <p className="devplan-offer-price">
                        <span className="devplan-price-strike">{offer.oldPriceText}</span>{" "}
                        <span className="devplan-price-free">{offer.priceText}</span>
                      </p>
                    ) : (
                      <p className="devplan-offer-price">{offer.priceText}</p>
                    )}
                    <ul className="devplan-offer-includes">
                      {offer.includes.map((item) => (
                        <li key={`${tier}-${item}`}>{item}</li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      onClick={() => {
                        void requireMutation(
                          () => openPurchaseModal(tier),
                          tier === "low" ?
                            "Sign up to unlock the free download package." :
                            "Sign up to continue with checkout."
                        );
                      }}
                      disabled={purchaseBusy || checkoutConfirming}
                    >
                      {tier === "low" ? (
                        <>
                          Get the Build Kit — <span className="devplan-price-strike">$99</span>{" "}
                          <span className="devplan-price-free">Free with signup</span>
                        </>
                      ) : (
                        `${offer.label} — ${offer.priceText}`
                      )}
                    </button>
                  </article>
                );
              })}
            </div>
            <p className="devplan-offer-footnote">
              Secure payment with Stripe for deposit tiers. The Technical Starter Kit is currently free with signup (normally $99).
            </p>
          </section>
        )}
      </div>

      {showPurchaseModal && (
        <div className="proposal-modal-overlay">
          <div className="proposal-modal">
            {selectedTier === "low" ? (
              <>
                <h2>Get the Technical Starter Kit</h2>
                <p className="devplan-modal-copy">
                  Sign up with email or Google to unlock the kit. Standard price is $99, currently free as part of onboarding.
                </p>
                <div className="devplan-offer-includes-box">
                  <h4>Includes</h4>
                  <ul>
                    {selectedOffer.includes.map((item) => (
                      <li key={`selected-${item}`}>{item}</li>
                    ))}
                  </ul>
                </div>
                <p className="devplan-modal-copy">
                  Price: <span className="devplan-price-strike">{selectedOffer.oldPriceText}</span>{" "}
                  <strong className="devplan-price-free">{selectedOffer.priceText}</strong>
                </p>
                <p className="devplan-modal-copy devplan-modal-copy-muted">
                  No payment required for this signup unlock.
                </p>
                <input
                  type="email"
                  value={purchaseEmail}
                  onChange={(event) => setPurchaseEmail(event.target.value)}
                  placeholder="you@company.com"
                  className="devplan-contact-input"
                  aria-label="Email for free download signup"
                  disabled={purchaseBusy}
                />
                {purchaseError && <div className="error">{purchaseError}</div>}
                <div className="proposal-modal-actions">
                  <button
                    type="button"
                    className="proposal-secondary-btn"
                    onClick={() => {
                      setShowPurchaseModal(false);
                      setPurchaseError(null);
                    }}
                    disabled={purchaseBusy}
                  >
                    Cancel
                  </button>
                  <button type="button" onClick={claimFreeDownload} disabled={purchaseBusy}>
                    {purchaseBusy ? "Preparing kit…" : "Unlock and download kit"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2>{selectedOffer.label}</h2>
                <p className="devplan-modal-copy">
                  {selectedOffer.description}
                </p>
                <div className="devplan-offer-includes-box">
                  <h4>Includes</h4>
                  <ul>
                    {selectedOffer.includes.map((item) => (
                      <li key={`selected-${item}`}>{item}</li>
                    ))}
                  </ul>
                </div>
                <p className="devplan-modal-copy">
                  Price: <strong>{selectedOffer.priceText}</strong>
                </p>
                <p className="devplan-modal-copy devplan-modal-copy-muted">
                  Signed-in checkout. Secure payment with Stripe.
                </p>
                <input
                  type="email"
                  value={purchaseEmail}
                  onChange={(event) => setPurchaseEmail(event.target.value)}
                  placeholder="you@company.com"
                  className="devplan-contact-input"
                  aria-label="Email for purchase confirmation"
                  disabled={purchaseBusy || checkoutConfirming}
                />
                {(selectedTier === "high" || selectedTier === "mid") && (
                  <textarea
                    className="proposal-modal-textarea"
                    rows={4}
                    value={purchaseNotes}
                    onChange={(event) => setPurchaseNotes(event.target.value)}
                    placeholder="Optional notes for our team (goals, timeline, constraints)"
                    disabled={purchaseBusy || checkoutConfirming}
                  />
                )}
                {purchaseError && <div className="error">{purchaseError}</div>}
                <div className="proposal-modal-actions">
                  <button
                    type="button"
                    className="proposal-secondary-btn"
                    onClick={() => {
                      setShowPurchaseModal(false);
                      setPurchaseError(null);
                    }}
                    disabled={purchaseBusy || checkoutConfirming}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!requireSignedInStatus("Create your account to continue with checkout.")) return;
                      void startCheckout();
                    }}
                    disabled={purchaseBusy || checkoutConfirming}
                  >
                    {purchaseBusy || checkoutConfirming ? "Preparing checkout…" : "Continue to Payment"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {editSection && (
        <div className="proposal-modal-overlay">
          <div className="proposal-modal">
            <h2>Edit section</h2>
            <textarea
              className="proposal-modal-textarea"
              rows={10}
              value={editValue}
              onChange={(event) => setEditValue(event.target.value)}
            />
            {editError && <div className="error">{editError}</div>}
            <div className="proposal-modal-actions">
              <button
                type="button"
                className="proposal-secondary-btn"
                onClick={() => {
                  setEditSection(null);
                  setEditError(null);
                }}
                disabled={editSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  void requireMutation(
                    () => saveEdit(),
                    "Create your account to save build plan changes."
                  );
                }}
                disabled={editSaving}
              >
                {editSaving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {authModal}
      <Footer />
    </>
  );
}
